import {
  ScrollView,
  TabBar,
  useFlags,
  useScrollToTopOnNavigate,
} from "@inductiveautomation/ws-components";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";

import AccountAppBar from "../../../components/AccountAppBar/AccountAppBar";
import { useGetMakerLicenseDetailsQuery } from "../api/makerLicenses.api";
import { AccountFlags } from "../../../featureFlags";
import { licenseActions } from "../Licenses.actions";
import { MakerLicenseAction } from "../Licenses.types";
import DeleteDialog from "../dialogs/DeleteDialog";
import GenerateActivationTokenDialog from "../dialogs/GenerateActivationTokenDialog";
import { getLicenseStyles } from "./_licenseStyle";
import { addDashToKey } from "./utilities";

const tabForPath = (pathname: string) => {
  if (pathname.endsWith("modules")) return "modules";
  if (pathname.endsWith("activations")) return "activations";
  if (pathname.endsWith("sessions")) return "sessions";

  return "details";
};

const License = () => {
  const { t } = useTranslation();
  const { classes } = getLicenseStyles();
  const [activeDialog, setActiveDialog] = useState<MakerLicenseAction>();
  const navigate = useNavigate();

  const { id } = useParams();
  const { tsAccountMakerLicenseFe } = useFlags<AccountFlags>();
  const { data, isLoading, error } = useGetMakerLicenseDetailsQuery(
    {
      licenseKey: id ?? "",
    },
    { skip: !id }
  );

  const { pathname } = useLocation();
  const scrollViewRef = useScrollToTopOnNavigate(pathname);

  useEffect(() => {
    if (!id) {
      void navigate("/maker-licenses");
    }

    if (
      (!tsAccountMakerLicenseFe &&
        (pathname.includes("modules") || pathname.includes("activations"))) ||
      pathname.includes("sessions")
    ) {
      void navigate("/404");
    }
  }, [id, navigate, tsAccountMakerLicenseFe, pathname]);

  const onAction = (actionId: string) => {
    setActiveDialog(actionId as MakerLicenseAction);
  };
  const closeDialog = () => setActiveDialog(undefined);

  if (error) {
    void navigate("/404");
    return;
  }

  return (
    <>
      <AccountAppBar
        title={[
          {
            label: t("maker_licenses.title"),
            id: "account-app-bar-maker-licenses",
            onClick: () => void navigate("/maker-licenses"),
          },
          {
            label: data ? addDashToKey(data.key) : "",
            loading: isLoading,
            id: "account-app-bar-maker-license",
            onClick: () => undefined,
            actionLists: data ? [...licenseActions(t, onAction)(data)] : [],
            chip: data?.status,
          },
        ]}
      />
      {!isLoading && (
        <div className={classes.content}>
          {tsAccountMakerLicenseFe && (
            <div className={classes.tabBarContainer}>
              <TabBar
                items={[
                  {
                    testId: "maker-license-details-tab",
                    label: t("maker_license.tabs.details.title"),
                    value: "details",
                  },
                  {
                    testId: "maker-license-modules-tab",
                    label: t("maker_license.tabs.modules.title"),
                    value: "modules",
                  },
                  {
                    testId: "maker-license-activations-tab",
                    label: t("maker_license.tabs.activations.title"),
                    value: "activations",
                  },
                  {
                    testId: "maker-license-sessions-tab",
                    label: t("maker_license.tabs.sessions.title"),
                    value: "sessions",
                  },
                ]}
                value={tabForPath(pathname)}
                onChange={(value) =>
                  void navigate(value === "details" ? "." : value)
                }
              />
            </div>
          )}

          <ScrollView ref={scrollViewRef}>
            <Outlet />
          </ScrollView>
        </div>
      )}

      <GenerateActivationTokenDialog
        open={activeDialog === "generate-activation-token"}
        licenses={data ? [data] : []}
        onClose={closeDialog}
        onSubmit={closeDialog}
      />
      <DeleteDialog
        open={activeDialog === "delete"}
        licenses={data ? [data] : []}
        onClose={closeDialog}
        onSubmit={() => {
          closeDialog();
          void navigate("/maker-licenses");
        }}
      />
    </>
  );
};

export default License;
